import { Check, ChevronDown, X } from 'lucide-react';
import {
  useDeferredValue,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSuppliers } from '@/features/suppliers/queries';
import type { Supplier } from '@/features/suppliers/types';
import { cn } from '@/lib/utils';

interface SupplierPickerProps {
  /** Selected supplier ids. The picker is fully controlled. */
  value: string[];
  onChange: (next: string[]) => void;
  /** Optional company scope to narrow the autocomplete (forwarded
   *  to the LIST query — backend then filters by visibility). */
  companyId?: string;
  disabled?: boolean;
  /** Customize the placeholder. Default: i18n
   *  `supplier.picker.searchPlaceholder`. */
  placeholder?: string;
  className?: string;
}

/**
 * SupplierPicker — multi-select autocomplete used by the upcoming
 * L2 part-form to attach suppliers to a part. Built on
 * `useSuppliers(filters)` so visibility narrowing is consistent
 * with the rest of the app — the list endpoint already does the
 * scope-aware filtering server-side.
 *
 * The selected items render as chips with an X to remove. Typing
 * filters the autocomplete; ArrowDown/Up moves the highlight,
 * Enter selects, Backspace on empty input removes the last chip.
 */
export function SupplierPicker({
  value,
  onChange,
  companyId,
  disabled,
  placeholder,
  className,
}: SupplierPickerProps) {
  const { t } = useTranslation();
  const inputId = useId();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const deferredQuery = useDeferredValue(query);
  const [highlighted, setHighlighted] = useState(0);

  const { data: suppliers } = useSuppliers({
    ...(deferredQuery.trim().length > 0 && { q: deferredQuery.trim() }),
    ...(companyId ? { scope: 'company', companyId } : {}),
  });

  // For chips we need names of items already selected, even if
  // they're not in the current `suppliers` slice (e.g. a chip
  // selected before the user started typing). Cache them locally.
  const [selectedCache, setSelectedCache] = useState<Map<string, Supplier>>(
    () => new Map(),
  );
  useEffect(() => {
    if (!suppliers) return;
    setSelectedCache((prev) => {
      const next = new Map(prev);
      for (const s of suppliers) {
        if (value.includes(s.id)) next.set(s.id, s);
      }
      return next;
    });
  }, [suppliers, value]);

  // Click-outside closes the dropdown.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const filtered = (suppliers ?? []).filter((s) => !value.includes(s.id));

  useEffect(() => {
    if (highlighted >= filtered.length) setHighlighted(0);
  }, [filtered.length, highlighted]);

  const toggle = (s: Supplier) => {
    if (value.includes(s.id)) {
      onChange(value.filter((id) => id !== s.id));
    } else {
      onChange([...value, s.id]);
      setQuery('');
    }
  };

  const removeAt = (id: string) => {
    onChange(value.filter((x) => x !== id));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setHighlighted((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const target = filtered[highlighted];
      if (target) toggle(target);
    } else if (e.key === 'Escape') {
      setOpen(false);
    } else if (e.key === 'Backspace' && query === '' && value.length > 0) {
      removeAt(value[value.length - 1]);
    }
  };

  return (
    <div ref={wrapperRef} className={cn('relative', className)}>
      <div
        className={cn(
          'flex flex-wrap items-center gap-1 rounded-md border border-input bg-background px-2 py-1 text-sm focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
          disabled && 'cursor-not-allowed opacity-50',
        )}
      >
        {value.map((id) => {
          const s = selectedCache.get(id);
          const label = s?.name ?? id.slice(0, 8);
          return (
            <span
              key={id}
              className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-xs"
            >
              {label}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => removeAt(id)}
                  aria-label={t('common.clear')}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3 w-3" aria-hidden />
                </button>
              )}
            </span>
          );
        })}
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          autoComplete="off"
          disabled={disabled}
          placeholder={placeholder ?? t('supplier.picker.searchPlaceholder')}
          value={query}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onKeyDown={handleKeyDown}
          role="combobox"
          aria-expanded={open}
          aria-controls={`${inputId}-listbox`}
          className="h-8 min-w-[8rem] flex-1 bg-transparent outline-none placeholder:text-muted-foreground"
        />
        <ChevronDown
          aria-hidden
          className="ml-auto h-4 w-4 text-muted-foreground"
        />
      </div>

      {open && filtered.length > 0 && (
        <ul
          id={`${inputId}-listbox`}
          role="listbox"
          className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-md border bg-popover p-1 text-sm shadow-md"
        >
          {filtered.slice(0, 50).map((s, idx) => {
            const isHighlighted = idx === highlighted;
            return (
              <li
                key={s.id}
                role="option"
                aria-selected={false}
                onMouseDown={(e) => {
                  e.preventDefault();
                  toggle(s);
                }}
                onMouseEnter={() => setHighlighted(idx)}
                className={cn(
                  'flex cursor-pointer items-center gap-2 rounded px-2 py-1.5',
                  isHighlighted && 'bg-accent text-accent-foreground',
                )}
              >
                <span className="flex-1 truncate">{s.name}</span>
                {s.code ? (
                  <span className="font-mono text-xs uppercase text-muted-foreground">
                    {s.code}
                  </span>
                ) : null}
                {value.includes(s.id) ? (
                  <Check className="h-4 w-4" aria-hidden />
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
